<template>
  <v-dialog
    :value="value"
    :persistent="isStreaming"
    max-width="800"
    content-class="add-role-dialog__content-class"
    @input="onDialogInput"
  >
    <v-card class="add-role-dialog" width="800">
      <!-- Modal header -->
      <div class="add-role-dialog__header">
        <div class="add-role-dialog__featured-icon" aria-hidden="true">
          <Icon name="org_icon_role" />
        </div>
        <div class="add-role-dialog__heading">
          <h2 class="add-role-dialog__title">
            <slot name="title">+ Add Role</slot>
          </h2>
        </div>
        <button
          type="button"
          class="add-role-dialog__close"
          aria-label="Close dialog"
          @click="onClose"
        >
          <v-icon>mdi-close</v-icon>
        </button>
      </div>

      <!-- Body -->
      <!-- Extra row above Role Name (e.g. the attach target) - only rendered when a consumer supplies it -->
      <div v-if="$slots['before-name']" class="add-role-dialog__attach-target">
        <slot name="before-name" />
      </div>

      <RoleNameContextBody
        :key="bodyKey"
        :name.sync="name"
        :context.sync="context"
        :existing-names="existingNames"
        :error-message="errorMessage"
        :show-replace-popover.sync="showReplacePopover"
        :max-name-characters="maxNameCharacters"
        name-id="add-role-dialog__name"
        context-id="add-role-dialog__context"
        @validity-change="onValidityChange"
        @streaming-change="onStreamingChange"
        @clear-error="emit('clearError')"
      />

      <!-- Footer -->
      <div class="add-role-dialog__footer">
        <button
          type="button"
          class="add-role-dialog__save"
          :disabled="!canSave"
          @click="handleSave"
        >
          <slot name="save-label">Save as Draft</slot>
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import RoleNameContextBody from "@components/RoleNameContextBody/RoleNameContextBody.vue";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "",
  },
  errorMessage: {
    type: String,
    default: "",
  },
  existingNames: {
    type: Array,
    default: () => [],
  },
  initialValue: {
    type: String,
    default: "",
  },
  maxNameCharacters: {
    type: Number,
    default: 120,
  },
});

const emit = defineEmits(["input", "onSave", "clearError"]);

const name = ref("");
const context = ref("");
const isStreaming = ref(false);
const isNameValid = ref(false);
const showReplacePopover = ref(false);
const bodyKey = ref(0);

const canSave = computed(() => isNameValid.value && !isStreaming.value);

function onValidityChange(value) {
  isNameValid.value = value;
}

function onStreamingChange(value) {
  isStreaming.value = value;
}

function onDialogInput(nextValue) {
  if (nextValue === true) {
    emit("input", true);
    return;
  }

  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }

  if (isStreaming.value) return;

  emit("clearError");
  emit("input", false);
}

function onClose() {
  if (isStreaming.value) return;
  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }
  emit("clearError");
  emit("input", false);
}

function handleSave() {
  if (!canSave.value) return;
  emit("onSave", { name: name.value.trim(), context: context.value.trim() });
}

watch(
  () => props.value,
  (open) => {
    if (!open) return;

    name.value = props.initialValue || "";
    context.value = "";
    showReplacePopover.value = false;
    bodyKey.value += 1;
  }
);

watch(
  () => props.initialValue,
  (next) => {
    if (props.value && next) name.value = next;
  }
);
</script>

<style scoped>
.add-role-dialog {
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.add-role-dialog__attach-target {
  padding: 24px 24px 0 24px;
}

.add-role-dialog__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 0 24px;
}

.add-role-dialog__featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #eaecf0;
}

.add-role-dialog__heading {
  flex: 1;
  min-width: 0;
}

.add-role-dialog__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
  text-align: center;
}

.add-role-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.add-role-dialog__close:hover,
.add-role-dialog__close:focus-visible {
  background: #f9fafb;
  outline: none;
}

.add-role-dialog__footer {
  padding: 0 24px 24px 24px;
}

.add-role-dialog__save {
  width: 100%;
  height: 44px;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  background: #7f56d9;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
}

.add-role-dialog__save:hover:not(:disabled),
.add-role-dialog__save:focus-visible {
  background: #6941c6;
  border-color: #6941c6;
  outline: none;
}

.add-role-dialog__save:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #475467;
  cursor: not-allowed;
}
</style>
